import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FEATURE_SLIDES } from "@/data/content";
import { resolveAssetPath } from "@/lib/assets";

export const FeatureCarousel = () => {
  const [idx, setIdx] = useState(0);
  const count = FEATURE_SLIDES.length;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  const prev = () => setIdx((i) => (i - 1 + count) % count);
  const next = () => setIdx((i) => (i + 1) % count);

  const s = FEATURE_SLIDES[idx];

  return (
    <section
      data-testid="feature-carousel"
      className="bg-white pb-12"
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="relative rounded-xl overflow-hidden h-[300px] md:h-[360px] shadow-lg">
          <img
            src={resolveAssetPath(s.image)}
            alt={s.title}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10" />

          <div className="relative h-full max-w-3xl px-6 md:px-10 py-8 flex flex-col justify-center text-white">
            <div className="text-[11px] uppercase tracking-wider text-white/85">
              {s.region}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="w-1 h-9 bg-[var(--yellow)]" />
              <h3 className="font-serif-display text-3xl md:text-4xl font-bold text-[var(--yellow)]">
                {s.title}
              </h3>
            </div>
            <div className="mt-3 text-sm text-white/90">{s.route}</div>
            <div className="mt-4 flex flex-wrap items-baseline gap-2 text-sm">
              <span className="font-semibold">{s.days}</span>
              <span className="text-white/60">|</span>
              <span>{s.date}</span>
              <span className="text-white/60">|</span>
              <span>from {s.from}</span>
              <span className="text-[var(--yellow)] font-serif-display text-2xl ml-2">
                {s.price}
              </span>
            </div>
            <div className="mt-1 text-[12px] text-white/85">{s.joining}</div>
            <p className="mt-3 max-w-xl text-[12px] text-white/80">
              Book your own flight tickets and join the tour directly at your
              destination — leave from the last destination.
            </p>
            <button
              data-testid="feature-carousel-cta"
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="mt-5 self-start bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold text-sm px-7 py-2.5 rounded transition"
            >
              Book now
            </button>
            <div className="mt-2 text-[10px] text-white/60">*T&C Apply</div>
          </div>

          {/* Controls */}
          <button
            type="button"
            onClick={prev}
            data-testid="carousel-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[var(--navy)] flex items-center justify-center shadow"
            aria-label="previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="carousel-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[var(--navy)] flex items-center justify-center shadow"
            aria-label="next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {FEATURE_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`slide ${i + 1}`}
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === idx ? "bg-[var(--yellow)] w-6" : "bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
